import React, { useCallback } from 'react';

type Listener = (state: [expanded: boolean]) => void;

export class ClientSideNavManager {
  private static instance: ClientSideNavManager;
  private static STATE_KEY = 'CLIENT_SIDENAV';
  private expanded: boolean = true;
  private listeners: Listener[] = [];

  private constructor() {
    const cached = sessionStorage.getItem(ClientSideNavManager.STATE_KEY);
    if (cached) {
      const cachedObj = JSON.parse(cached);
      this.expanded = cachedObj['expanded'];
    }
  }

  public setExpanded(value: boolean) {
    if (value === this.expanded) {
      return;
    }

    this.expanded = value;
    this.cache();
    this.notify();
  }

  public subscribe(listener: Listener): void {
    this.listeners.push(listener);
    listener([this.expanded]);
  }

  public unsubscribe(listener: Listener): void {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  private cache(): void {
    sessionStorage.setItem(
      ClientSideNavManager.STATE_KEY,
      JSON.stringify({
        expanded: this.expanded,
      })
    );
  }

  private notify(): void {
    this.listeners.forEach(listener => listener([this.expanded]));
  }

  public static getInstance(): ClientSideNavManager {
    if (!ClientSideNavManager.instance) {
      ClientSideNavManager.instance = new ClientSideNavManager();
    }
    return ClientSideNavManager.instance;
  }

  public static reset(): void {
    ClientSideNavManager.instance = new ClientSideNavManager();
  }
}

type SideNavOptions = {
  /**
   * The initial state (expanded/collapsed) of the sidebar. Defaults to false (collapsed).
   * This value is only used on first render, as long as the state is not persisted in session storage.
   * Subsequent renders of the component will use the state from session storage.
   */
  expanded?: boolean;
};

function useClientSideNav(options?: SideNavOptions) {
  const [expandedState, setExpandedState] = React.useState(true);

  const listener = (state: [expanded: boolean]) => {
    const [expanded] = state;
    setExpandedState(expanded);
  };

  React.useEffect(() => {
    ClientSideNavManager.getInstance().subscribe(listener);
    return () => {
      ClientSideNavManager.getInstance().unsubscribe(listener);
    };
  }, []);

  const setExpanded = useCallback((state: boolean) => {
    ClientSideNavManager.getInstance().setExpanded(state);
  }, []);

  React.useEffect(() => {
    if (options?.expanded !== undefined) {
      ClientSideNavManager.getInstance().setExpanded(options.expanded);
    }
  }, [options?.expanded]);

  return {
    expanded: expandedState,
    setExpanded,
  };
}

export { useClientSideNav };
